/**
 * Projects tool section shells — Personal | Team (sidebar sub-nav split,
 * mirroring Time Tracking / Signatures).
 *
 * Personal (/projects) — for EVERYONE incl. admins, participating-only scope:
 *   Portfolio (mine) · My Items · Requests · Timeline (mine)
 *   Requests stays under Personal: the page already branches (admin inbox +
 *   own requests in one view) and non-admin submitters need their
 *   own-requests view here.
 * Team (/projects/team) — Projects admins only (full portfolio):
 *   Portfolio (all) · Timeline (all) · Overdue & Tasks (incl. the cross-project
 *   open-action-items overview grouped by assignee)
 */
import { Navigate } from 'react-router-dom';
import { type ReactNode } from 'react';
import { SectionTabs, type SectionTab } from '../components/SectionTabs';
import { useProfile } from '../profile/ProfileProvider';
import { useIsProjectsAdmin } from './useIsProjectsAdmin';

export type ProjectsScope = 'personal' | 'team' | 'admin';

export function ProjectsSection({ scope }: { scope: ProjectsScope }) {
  const tabs: SectionTab[] =
    scope === 'personal'
      ? [
          { to: '/projects', label: 'Portfolio', end: true },
          { to: '/projects/items', label: 'My Items' },
          { to: '/projects/requests', label: 'Requests' },
          { to: '/projects/timeline', label: 'Timeline' },
        ]
      : scope === 'team'
      ? [
          { to: '/projects/team', label: 'Portfolio', end: true },
          { to: '/projects/team/timeline', label: 'Timeline' },
          { to: '/projects/team/overdue', label: 'Overdue & Tasks' },
        ]
      : [{ to: '/projects/admin', label: 'Link types', end: true }];

  return <SectionTabs title="Projects" tabs={tabs} />;
}

/**
 * Route guard for the Team side: Projects admins only (same set as the
 * sidebar Team entry — tools.projects.role approver/approver_admin OR
 * app/hub admin). Non-admins are redirected to their Personal view.
 */
export function RequireProjectsAdmin({ children }: { children: ReactNode }) {
  const { state } = useProfile();
  const isAdmin = useIsProjectsAdmin();
  if (state.status !== 'ready') return null;
  if (!isAdmin) return <Navigate to="/projects" replace />;
  return <>{children}</>;
}
